import { openDB, type IDBPDatabase } from "idb";
import { befehlSenden, type Umschlag } from "./api";

/**
 * Befehlswarteschlange (ADR-0020): Meldungen liegen in IndexedDB, bis sie angenommen oder abgewiesen sind; dieselbe
 * Meldungs-Id bei jeder Wiederholung (befehle.md §5). Übertragen wird in der Reihenfolge der Erfassung: beim Start der
 * App, beim Einreihen, sofort beim Ereignis `online` und nach einem gescheiterten Durchgang mit wachsender Wartezeit
 * (2, 4, 8, 16, 30, 30 … Sekunden; Anforderungen K93 c, teststeuerung.md §10). Den Übertragungsstand zeigt die Ansicht
 * „Meldungen“ (oberflaeche.md §10).
 *
 * Zwei Ebenen der Wiederholung: Bricht eine einzelne Anfrage ohne Antwort ab, sendet `befehlSenden` sie bei Netz einmal
 * nach 300 ms neu (api.ts); scheitert der Befehl auch dann (oder antwortet der Dienst 401 bzw. 5xx), endet der Durchgang,
 * und die Warteschlange versucht es nach der Wartezeit erneut. `online` setzt die Wartezeit zurück.
 */
export type Uebertragungsstand = "noch nicht übertragen" | "übertragen" | "abgewiesen";

/** Symbol je Übertragungsstand (design/README §8): Umschlag mit X, Sanduhr, Haken. */
export const STANDSYMBOL: Record<Uebertragungsstand, string> = {
  abgewiesen: "abgewiesen", "noch nicht übertragen": "warteschlange", "übertragen": "uebertragen",
};

export interface Eintrag extends Umschlag {
  befehl: string;
  /** Titel nach oberflaeche.md §1.5, z. B. `Schuss S-1101`, `Stand 7`, `Nachricht` */
  titel: string;
  stand: Uebertragungsstand;
  grund: string | null;
  erfasst: number;
  /**
   * Laufende Nummer der Erfassung auf diesem Gerät: legt die Reihenfolge fest, auch wenn zwei Meldungen in derselben
   * Millisekunde bzw. bei stehender Gerätezeit erfasst werden (`erfasst` gleich, K16)
   */
  folge?: number;
  /** Datei, die vor dem Befehl hochzuladen ist (z. B. Anhang einer Nachricht, medien.md §4 Schritt 4) */
  datei?: Blob;
}

/**
 * Vorbereitung eines Eintrags vor dem Senden (z. B. Upload-Platz anfordern und hochladen, medien.md §4): liefert die
 * Daten des Befehls, `null` bei fehlendem Netz (der Eintrag bleibt in der Reihenfolge stehen) oder einen Grund, mit dem
 * der Eintrag abgewiesen ist. Registriert vom Kontext, dem der Befehl gehört.
 */
export type Vorbereitung = (e: Eintrag) => Promise<{ daten: Record<string, unknown> } | { grund: string } | null>;
const vorbereitungen = new Map<string, Vorbereitung>();

export function vorbereitungRegistrieren(befehl: string, v: Vorbereitung): void {
  vorbereitungen.set(befehl, v);
}

const DB = "dj-warteschlange";
let verbindung: Promise<IDBPDatabase> | null = null;
const beobachter = new Set<() => void>();
let laeuft: Promise<void> | null = null;
let erneut = false;

/** Wartezeit nach dem ersten gescheiterten Durchgang; jede weitere verdoppelt sie bis höchstens 30 Sekunden (K93 c). */
export const ERSTE_WARTEZEIT_MS = 2_000;
export const LAENGSTE_WARTEZEIT_MS = 30_000;
let wartezeitMs = ERSTE_WARTEZEIT_MS;
let wecker: ReturnType<typeof setTimeout> | null = null;

function db(): Promise<IDBPDatabase> {
  verbindung ??= openDB(DB, 1, {
    upgrade(d) {
      const s = d.createObjectStore("befehle", { keyPath: "meldungsId" });
      s.createIndex("erfasst", "erfasst");
    },
  });
  return verbindung;
}

function melden(): void {
  for (const b of beobachter) b();
}

export function beobachten(f: () => void): () => void {
  beobachter.add(f);
  return () => beobachter.delete(f);
}

export async function eintraege(): Promise<Eintrag[]> {
  const alle = (await (await db()).getAllFromIndex("befehle", "erfasst")) as Eintrag[];
  return alle.sort((a, b) => a.erfasst - b.erfasst || (a.folge ?? 0) - (b.folge ?? 0));
}

/** Meldung in die Warteschlange legen und sofort zu übertragen versuchen. */
export async function einreihen(befehl: string, titel: string, u: Umschlag, datei?: Blob): Promise<void> {
  const tx = (await db()).transaction("befehle", "readwrite");
  const folge = Math.max(0, ...((await tx.store.getAll()) as Eintrag[]).map((e) => e.folge ?? 0)) + 1;
  await tx.store.put({
    ...u, befehl, titel, stand: "noch nicht übertragen", grund: null, erfasst: Date.now(), folge, ...(datei ? { datei } : {}),
  } satisfies Eintrag);
  await tx.done;
  melden();
  void uebertragen();
}

/** Eintrag entfernen, z. B. eine abgewiesene Meldung, die die Person geklärt hat (oberflaeche.md §10). */
export async function entfernen(meldungsId: string): Promise<void> {
  await (await db()).delete("befehle", meldungsId);
  melden();
}

/**
 * Überträgt alle wartenden Einträge der Reihe nach; bricht beim ersten Netzfehler ab (Reihenfolge bleibt). Kommt während
 * einer Übertragung ein Anstoß (Einreihen, `online`), folgt gleich danach ein weiterer Durchgang, auch wenn dieser
 * gescheitert ist (sonst wartete etwa eine Meldung, deren Netz während des Versuchs zurückkam, bis zum nächsten Versuch).
 * Scheitert der letzte Durchgang, plant die Warteschlange den nächsten nach der Wartezeit.
 */
export function uebertragen(): Promise<void> {
  if (laeuft) {
    erneut = true;
    return laeuft;
  }
  weckerAbstellen();
  laeuft = (async () => {
    let gelungen = false;
    try {
      do {
        erneut = false;
        gelungen = false;
        gelungen = await durchgang();
      } while (erneut);
    } finally {
      laeuft = null;
      naechstenVersuchPlanen(gelungen);
    }
  })();
  return laeuft;
}

function weckerAbstellen(): void {
  if (wecker !== null) clearTimeout(wecker);
  wecker = null;
}

function naechstenVersuchPlanen(gelungen: boolean): void {
  if (gelungen) {
    wartezeitMs = ERSTE_WARTEZEIT_MS;
    return;
  }
  wecker = setTimeout(() => void uebertragen(), wartezeitMs);
  wartezeitMs = Math.min(wartezeitMs * 2, LAENGSTE_WARTEZEIT_MS);
}

/** Das Gerät meldet wieder Netz: sofort übertragen, die Wartezeit beginnt wieder bei 2 Sekunden. */
export function netzZurueck(): Promise<void> {
  wartezeitMs = ERSTE_WARTEZEIT_MS;
  return uebertragen();
}

/** Ein Durchgang über die wartenden Einträge; `false` nach einem Netzfehler (dann wartet der Rest). */
async function durchgang(): Promise<boolean> {
  for (const e of await eintraege()) {
    if (e.stand !== "noch nicht übertragen") continue;
    const vorbereitung = e.datei ? vorbereitungen.get(e.befehl) : undefined;
    if (vorbereitung) {
      const v = await vorbereitung(e);
      if (v === null) return false;
      if ("grund" in v) {
        await (await db()).put("befehle", { ...e, stand: "abgewiesen", grund: v.grund });
        melden();
        continue;
      }
      e.daten = v.daten;
      delete e.datei;
      await (await db()).put("befehle", e);
    }
    const a = await befehlSenden(e.befehl, { meldungsId: e.meldungsId, geraetezeit: e.geraetezeit, daten: e.daten });
    if (a.netzfehler || a.status === 401 || a.status >= 500) return false;
    await (await db()).put("befehle", { ...e, stand: a.ok ? "übertragen" : "abgewiesen", grund: a.ok ? null : a.grund });
    melden();
  }
  return true;
}

/** Läuft gerade eine Übertragung (ein Befehl ist unterwegs)? Der Lebenszyklus lädt dann nicht neu (ADR-0041 §3). */
export function uebertraegt(): boolean {
  return laeuft !== null;
}

export async function wartend(): Promise<number> {
  return (await eintraege()).filter((e) => e.stand === "noch nicht übertragen").length;
}

/** Startet die Übertragung beim Start der App und sofort bei `online`; nach einem Fehlschlag plant sie sich selbst. */
export function warteschlangeStarten(): void {
  window.addEventListener("online", () => void netzZurueck());
  void uebertragen();
}
